import { MobileHelper } from '@rightship-tech/common-ui/helpers/mobile-helper';
import { DataGridColumn } from '@rightship-tech/common-ui/components/comp-data-grid/comp-data-grid.models';
import { DataType, ItemSource, SourceType } from '@rightship-tech/common-ui/models/queries.models';
import { autoinject, bindable, computedFrom, transient } from 'aurelia-framework';
import { AccessTypeEnum, DocumentManagementEntityTypeEnum, DocumentMetaModel, FormMetaModel, RequirementModel } from '../../../models/document-management.models';
import { DocumentRequirementApi } from '../../../apis/document-requirement.api';
import moment from 'moment';
import Enumerable from 'linq';
import { DialogClientService } from '@rightship-tech/common-ui/services/basic/dialog-client.service';
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { DocumentManagementDialogConstants } from '../../../constants/dialog.constants';
import { Router } from 'aurelia-router';
import { DocumentManagementRoutes } from '../../../constants/route.constant';
import { DocumentManagementApi } from '../../../apis/document-management.api';
import { DocumentManagmentClaims } from '../../../constants/claims.constants';
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import { LookupClientService } from '@rightship-tech/common-ui/services/lookups/lookup-client.service';
import { LookupValueRequest, LookupValueResponse } from '@rightship-tech/common-ui/models/lookup.models';
import { VesselModel } from '../../../models/my-vessels.models';
import { DocumentReviewApi } from '../../../apis/document-review.api';
import { ReviewClientModel } from '../../../models/document-review.models';
import { CompDataGrid } from '@rightship-tech/common-ui/components/comp-data-grid/comp-data-grid';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';

@autoinject
@transient()
export class PartialDocumentsManagement {

    private _formTypes: { [id: number]: string } = {};
    private _documentTypes: { [id: number]: string } = {};
    private _documentsAndForms: RequirementModel[] = [];
    private _filterdRequirements: RequirementModel[] = [];
    private _canDeleteDocumenUploadFromVesselDocumentTab: boolean = false;
    private _canDeleteDocumentsFromDocumentManagementCentre: boolean = false;
    private _customerReviews: { [customerId: number]: ReviewClientModel[] } = {};

    public itemSource: ItemSource = null;
    public selectedFilterType: string = 'all-documents';
    public isRsUser: boolean = false;
    public dataGridVm: CompDataGrid;

    @bindable
    public vesselId: number;

    @bindable
    public selectedRequirementId: number;

    @bindable
    public vessel: VesselModel;

    public get customerId(): number {
        return this.userClienService?.currentUser?.businessUnit?.customerId ?? -1;
    }

    public get userId(): number {
        return this.userClienService?.currentUser?.user?.id ?? -1;
    }

    constructor(private docuementRequirementApi: DocumentRequirementApi, private dialogService: DialogClientService,
        private router: Router, private documentManagementApi: DocumentManagementApi, private userClaimsService: UserClaimsService,
        private lookupClientService: LookupClientService, private userClienService: UserClientService,
        private documentReviewApi: DocumentReviewApi, private googleAnalyticsService: GoogleAnalyticsService) {

        this._canDeleteDocumenUploadFromVesselDocumentTab = this.userClaimsService.hasPermission(DocumentManagmentClaims.CanDeleteDocumenUploadFromVesselDocumentTab);
        this._canDeleteDocumentsFromDocumentManagementCentre = this.userClaimsService.hasPermission(DocumentManagmentClaims.CanDeleteDocumentsFromDocumentManagementCentre);
        this.isRsUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    async attached() {
        //Refresh data
        this.refreshData(true);
    }

    @computedFrom('_documentsAndForms', '_documentsAndForms.length')
    get requirementsCategories() {
        return Enumerable.from(this._documentsAndForms)
            .where(x => this.isRsUser || x.subscribedCustomerId == this.customerId || x.requirementCustomerId == this.customerId)
            .distinct(x => x.requirementId)
            .select(x => {
                return {
                    requirementId: x.requirementId,
                    requirementName: x.requirementName
                };
            }).toArray();
    }

    @computedFrom('_documentsAndForms', '_documentsAndForms.length')
    public get allRequirementsCount() {
        return this.getAllDocuments().length;
    }

    @computedFrom('_documentsAndForms', '_documentsAndForms.length')
    public get pendingRequirementsCount() {
        return this.getPendingDocuments().length;
    }

    getAllRequirementsCount(requirementId: number) {
        return this.getDocumentsByRequirementId(requirementId).length;
    }

    getSubmittedRequirementsCount(requirementId: number) {
        return this.getDocumentsByRequirementId(requirementId).filter(x => this.isRequirementReady(x) && !this.isRequirementExpired(x)).length;
    }

    getRequirementProgress(requirementId: number) {
        const submittedDocsCount = this.getSubmittedRequirementsCount(requirementId);
        const allDocsCount = this.getAllRequirementsCount(requirementId);
        return allDocsCount == 0 ? 0 : Math.round((submittedDocsCount / allDocsCount) * 100);
    }

    private getDocumentName(item: RequirementModel): string {
        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
            const id = (<FormMetaModel>item.meta)?.formTypeId;
            return this._formTypes[id] ?? item.requirementName;
        }
        else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
            const id = (<DocumentMetaModel>item.meta)?.documentTypeId;
            return this._documentTypes[id] ?? item.requirementName;
        }
    }

    public getColumns() {
        let columns = [];

        columns.push(
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                name: "documentName",
                title: MobileHelper.isMobileDevice() ? "" : "Document",
                value: `<div class="requirement">
                        <div class="row">
                            <span class="requirement-name" if.bind="!isRequirementReady(item) || !hasAccess(item)">\${getDocumentName(item)}</span>
                            <a class="link requirement-name" if.bind="isRequirementReady(item) && hasAccess(item)" click.delegate="handleItemClick(item)">\${getDocumentName(item)}</a>
                            
                            <comp-icon ref="requirementRef" class="icon-info missing-document \${isExpired(item) ? 'is-expired':''}" icon="info-exclamation-mark-filled" 
                                if.bind="!isRequirementReady(item) || isExpired(item)"></comp-icon>
                            <comp-tooltip on-element.bind="requirementRef" show-arrow="true" if.bind="requirementRef">
                                <div class="column" if.bind="isExpired(item)">
                                    <span if.bind="item.entityType == formType">Form has expired, please complete a new form.</span>
                                    <span if.bind="item.entityType == documentType">Document has expired, please upload an updated one.</span>

                                    <a class="link" if.bind="item.entityType == documentType && canUploadDocument(item)" click.delegate="openUploadDocumentDialog(item)">Upload new document</a>
                                    <a class="link" if.bind="item.entityType == formType && canCompleteVesselForm(item)" click.delegate="completeVesselForm(item)">Complete Form</a>
                                </div>
                                <div class="column" if.bind="!isExpired(item)">
                                    <span class="description margin-bottom-24">\${item.description}</span>
                                    <span class="validity" if.bind="item.documentValidityInDays > 0">Validity: \${item.documentValidityInDays} days</span>
                                    <span class="validity" if.bind="item.documentValidityInDays <= 0">Validity: Always Valid</span>
                                </div>
                            </comp-tooltip>
                        </div>
                        
                        <span class="issue-date" if.bind="item.entityType == documentType && item.meta.issueDate">Issue Date: \${formatDate(item.meta.issueDate)}</span>
                        <div class="column status \${getStatusClass(item.meta.formStatus)}" if.bind="item.entityType == formType && item.meta.formStatus">
                            <div class="row">
                                <span class="status-badge-dot"></span>
                                <span class="status-text">\${item.meta.formStatus}</span>
                            </div>
                            <span class="status-date">\${getDateToString(item.meta.formStatusDate)}</span>
                        </div>
                    </div>`,
                columnOrder: 1,
                instancePerRow: true,
                parameter: {
                    item: null,
                    documentType: DocumentManagementEntityTypeEnum.VesselDocument,
                    formType: DocumentManagementEntityTypeEnum.VesselForm,
                    getDateToString: (date) => { return this.getDateToString(date); },
                    getStatusClass: (status) => { return status?.toLowerCase()?.replace(/[^a-zA-Z0-9]+/g, '-'); },
                    getDocumentName: (item: RequirementModel) => {
                        return this.getDocumentName(item);
                    },
                    isRequirementReady: (item: RequirementModel) => {
                        return this.isRequirementReady(item);
                    },
                    canCompleteVesselForm: (item: RequirementModel) => {
                        return this.canCompleteVesselForm(item);
                    },
                    isExpired: (item: RequirementModel) => {
                        return this.isRequirementExpired(item);
                    },
                    openUploadDocumentDialog: (item) => {
                        this.openUploadDocumentDialog(item);
                    },
                    canUploadDocument: (item: RequirementModel) => {
                        return this.customerId == item.uploadedByCustomerId;
                    },
                    completeVesselForm: async (item) => {
                        await this.completeVesselForm(item);
                    },
                    handleItemClick: async (item: RequirementModel) => {
                        const itemName = this.getDocumentName(item);
                        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
                            this.router.navigateToRoute(DocumentManagementRoutes.FormViewer.replace("/:onboardingRequestId", ""), { onboardingRequestId: (<FormMetaModel>item.meta).onboardingRequestId });
                        }
                        else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
                            this.downloadDocument(item, itemName);
                        }
                    },
                    formatDate: (date: Date | string) => {
                        return date ? moment.utc(date).local().format("DD MMM YYYY") : "-";
                    },
                    hasAccess: (item: RequirementModel) => {
                        return this.hasDocumentAccess(item);
                    },
                }
            });

        columns.push(
            <DataGridColumn>{
                dataType: DataType.DateTime,
                name: "uploadedDate",
                title: "Date Uploaded",
                columnOrder: 2,
                valueConverter: (value: any, item: RequirementModel, params) => {
                    return value ? moment.utc(value).local().format("DD MMM YYYY HH:mm") : "-";
                }
            });

        columns.push(<DataGridColumn>{
            dataType: DataType.CustomTemplate,
            name: "uploadedByUser",
            title: "Uploaded By",
            value: `<div class="uploaded-by" if.bind="item.uploadedByUser">
                        <span class="name">\${item.uploadedByUser}</span>
                        <span class="customer-name">\${item.uploadedByCustomer}</span>
                    </div>
                    <span class="uploaded-by" if.bind="!item.defaultAccessTypeId">-</span>`,
            columnOrder: 3,
            instancePerRow: true,
            parameter: {
                item: null
            }
        });

        columns.push(<DataGridColumn>{
            dataType: DataType.Date,
            name: "documentValidityInDays",
            title: "Expires On",
            columnOrder: 4,
            valueConverter: (value: number, item: RequirementModel, params) => {
                if (!this.isRequirementReady(item)) {
                    return "-";
                }
                const issuedDate = item.entityType == DocumentManagementEntityTypeEnum.VesselForm ? null : (<DocumentMetaModel>item.meta).issueDate;
                if (!item.meta.expiryDate && item.uploadedDate && value == 0) return 'Always Valid';
                const expiryDate = this.getDocumentExpiryDate(item.meta.expiryDate, issuedDate ?? item.uploadedDate, value);
                return expiryDate ? moment.utc(expiryDate).local().format("DD MMM YYYY") : "-";
            }
        });

        columns.push(<DataGridColumn>{
            dataType: DataType.CustomTemplate,
            name: "defaultAccessTypeId",
            title: "Access",
            value: `<template if.bind="canSeeDocumentAccessSetting(item)">
                        <div class="access row nowrap" if.bind="item.defaultAccessTypeId">
                            <span class="text">\${hasMixedAccess(item)? 'Mixed Access': item.defaultAccessTypeName}</span>
                            <comp-icon ref="accessTypeRef" class="icon-info" icon="info" if.bind="hasMixedAccess(item)"></comp-icon>                        
                            <comp-tooltip on-element.bind="accessTypeRef" if.bind="accessTypeRef">
                                <div class="column">
                                    <span class="description">\${getMixedAccessDescription(item)}</span>
                                    <a class="link" if.bind="item.uploadedByCustomerId == customerId || item.uploadedByUserId == userId" click.delegate="openManageAccess(item)">Manage Access</a>
                                </div>
                            </comp-tooltip>
                        </div>
                        <a class="link row" if.bind="item.accessRequestCount > 0 && (item.uploadedByCustomerId == customerId || item.uploadedByUserId == userId)" click.delegate="openManageAccess(item)">Review access requests (\${item.accessRequestCount})</a>
                        <span class="access" if.bind="!item.defaultAccessTypeId">-</span>
                    </template>
                    <template if.bind="!canSeeDocumentAccessSetting(item)">
                        <a class="link" if.bind="!item.actionLoading && canRequestAccess(item)" click.trigger="requestAccess(item)">Request Access</a>
                        <span if.bind="hasAccess(item)">Available</span>
                        <div if.bind="hasPendingAccessRequest(item)">
                            <span class="row">Requested by:</span>
                            <span class="row">\${item.pendingAccessRequest.userName}</span>
                            <span class="row" if.bind="item.pendingAccessRequest.requestedDate">\${formatDate(item.pendingAccessRequest.requestedDate)}</span>
                        </div>
                    </template>`,
            columnOrder: 6,
            instancePerRow: true,
            parameter: {
                item: null,
                customerId: this.customerId,
                userId: this.userId,
                hasMixedAccess: (item: RequirementModel) => {
                    return Enumerable.from([item.defaultAccessTypeId].concat(item.accessTypes.map(x => x.accessTypeId))).where(x => x != null).distinct().count() > 1;
                },
                getMixedAccessDescription: (item: RequirementModel) => {
                    const canAccessCount = item.accessTypes.filter(x => x.accessTypeId == AccessTypeEnum.AccessAllowed).length;
                    const accessOnDemandCount = item.accessTypes.filter(x => x.accessTypeId == AccessTypeEnum.AccessOnDemand).length;
                    const deniedAccessCount = item.accessTypes.filter(x => x.accessTypeId == AccessTypeEnum.DenyAccess).length;

                    let description = 'Warning: According to the current access rules, ';
                    if (canAccessCount > 0)
                        description += `${canAccessCount} requesting party has access`;
                    if (accessOnDemandCount > 0)
                        description += `${canAccessCount > 0 && deniedAccessCount > 0 ? ',' : (canAccessCount > 0 ? ', and' : '')} ${accessOnDemandCount} ${canAccessCount > 0 ? 'others' : 'requesting party'} can see the document listed but cannot access unless they request accesss`;
                    if (deniedAccessCount > 0)
                        description += `${canAccessCount > 0 || accessOnDemandCount > 0 ? ', and' : ''} ${deniedAccessCount} ${canAccessCount > 0 || accessOnDemandCount > 0 ? 'others' : 'requesting party'}  are denied access`;
                    return description;

                },
                openManageAccess: (item: RequirementModel) => {
                    this.openManageAccessDialog(item, true);
                },
                canSeeDocumentAccessSetting: (item: RequirementModel) => {
                    return this.canSeeDocumentAccess(item);
                },
                canRequestAccess: (item: RequirementModel) => {
                    return this.shouldRequestDocumentAccess(item);
                },
                requestAccess: (item: RequirementModel) => {
                    this.openRequestAccessDialog(item);
                },
                hasAccess: (item: RequirementModel) => {
                    return this.hasDocumentAccess(item);
                },
                hasPendingAccessRequest: (item: RequirementModel) => {
                    return this.isRequirementReady(item) && item.hasPendingAccessRequest;
                },
                formatDate: (date: Date | string) => {
                    return date ? moment.utc(date).local().format("DD MMM YYYY") : "-";
                }
            }
        });
        
        if(this.isRsUser || this._filterdRequirements.some(x => x?.requestingParties?.indexOf(this.customerId) > -1))
        {
            columns.push(<DataGridColumn>{
                dataType: DataType.CustomTemplate,
                name: "review",
                title: "Reviewed By",
                isHidden: (item: RequirementModel) => {
                    return !(this.isRsUser || item?.requestingParties?.indexOf(this.customerId) > -1);
                },
                value: `<div class="review row nowrap" if.bind="item.review.isReviewed">
                            <comp-icon ref="reviewRef" class="icon-reviewed" icon="person-tick"></comp-icon>                        
                            <comp-tooltip on-element.bind="reviewRef" if.bind="reviewRef">
                                <div class="column">
                                    <b>Reviewed by \${item.review.reviewedBy}:</b>
                                    <span>\${item.review.comments}</span>
                                    <span class="review-date">\${formatDate(item.review.reviewedDate)}</span>
                                </div>
                            </comp-tooltip>
                        </div>
                        <span class="review" if.bind="!item.review.isReviewed">-</span>`,
                columnOrder: 5,
                instancePerRow: true,
                parameter: {
                    item: null,
                    formatDate: (date: Date | string) => {
                        return moment.utc(date).local().format("DD MMM YYYY HH:mm");
                    }
                }
            });
        }

        columns.push(<DataGridColumn>{
            dataType: DataType.CustomTemplate,
            name: "meta",
            title: "Actions",
            value: `<div class="actions align-items-middle">
                        <a class="link" if.bind="!item.actionLoading && isDocument(item) && canUploadDocument(item)" click.trigger="openUploadDocumentDialog(item)">Upload Document</a>
                        <a class="link" if.bind="!item.actionLoading && isForm(item) && canCompleteVesselForm(item)" click.trigger="completeVesselForm(item)">Complete Form</a>
                        <span if.bind="!isDocument(item) && !isForm(item) && !canRequestAccess(item) && !isRequirementReady(item)">-</span>

                        <comp-loader if.bind="item.actionLoading"></comp-loader>

                        <div class="row justify-content-end align-items-middle" if.bind="isRequirementReady(item)">
                            <comp-list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </comp-list-menu>
                        </div>
                    </div>`,
            columnOrder: 7,
            instancePerRow: true,
            parameter: {
                item: null,
                canRequestAccess: (item: RequirementModel) => {
                    return this.shouldRequestDocumentAccess(item);
                },
                requestAccess: (item: RequirementModel) => {
                    this.openRequestAccessDialog(item);
                },
                canUploadDocument: (item: RequirementModel) => {
                    return this.canUploadDocument(item);
                },
                canCompleteVesselForm: (item: RequirementModel) => {
                    return this.canCompleteVesselForm(item);
                },
                isRequirementReady: (item: RequirementModel) => { return this.isRequirementReady(item); },
                isDocument: (item: RequirementModel) => { return item.entityType == DocumentManagementEntityTypeEnum.VesselDocument; },
                isForm: (item: RequirementModel) => { return item.entityType == DocumentManagementEntityTypeEnum.VesselForm; },
                openUploadDocumentDialog: (item) => {
                    this.openUploadDocumentDialog(item);
                },
                completeVesselForm: async (item) => {
                    await this.completeVesselForm(item);
                },
                menuItems: (item: RequirementModel) => {
                    return [
                        {
                            item: { text: "Upload Document" },
                            classList: 'upload-document',
                            click: () => {
                                this.openUploadDocumentDialog(item);
                            },
                            isVisible: () => { return item.entityType == DocumentManagementEntityTypeEnum.VesselDocument && this.isRequirementExpired(item) && item.subscribedCustomerId == this.customerId; }
                        },
                        {
                            item: { text: "Complete Form" },
                            classList: '',
                            click: async () => {
                                await this.completeVesselForm(item);
                            },
                            //Hidden as part of https://rightship.atlassian.net/browse/ET1-1213
                            isVisible: () => { return false; }
                        },
                        {
                            item: { text: "Mark as Reviewed" },
                            classList: '',
                            click: () => {
                                this.openReviewDialog(item, true);
                            },
                            isVisible: () => { return item?.hasAccess && ((item?.review?.isReviewed ?? false) == false) && item?.requestingParties?.indexOf(this.customerId) > -1; }
                        },
                        {
                            item: { text: "Mark as Unreviewed" },
                            classList: '',
                            click: () => {
                                this.openReviewDialog(item, false);
                            },
                            isVisible: () => { return item?.hasAccess && ((item?.review?.isReviewed ?? false) == true) && item?.requestingParties?.indexOf(this.customerId) > -1; }
                        },
                        {
                            item: { text: "View History" },
                            classList: '',
                            click: () => {

                                this.dialogService.show(DocumentManagementDialogConstants.AuditsDialog, { documentId: this.getItemId(item), documentTypeId: item.entityType }, false, false, false, 'right');
                            },
                            isVisible: () => { return this.isRequirementReady(item) && (item.subscribedCustomerId == this.customerId || item?.requestingParties?.indexOf(this.customerId) > -1); }
                        },
                        {
                            item: { text: "Manage Access" },
                            classList: '',
                            click: () => {
                                this.openManageAccessDialog(item, true);
                            },
                            isVisible: () => { return this.isRequirementReady(item) && item.entityType == DocumentManagementEntityTypeEnum.VesselDocument && item?.uploadedByCustomerId == this.customerId; }
                        },
                        {
                            item: { text: "Delete" },
                            classList: '',
                            click: async () => {
                                this.dialogService.show(DocumentManagementDialogConstants.DeleteDocumentConfirmationDialog, {
                                    name: this.getDocumentName(item),
                                    onDelete: (async () => {
                                        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
                                            await this.documentManagementApi.deleteForm(this.getItemId(item));
                                        }
                                        if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
                                            await this.documentManagementApi.deleteDocument(this.getItemId(item));
                                        }
                                        await this.refreshData();
                                    }).bind(this)
                                }, true);
                            },
                            isVisible: () => { return ((this._canDeleteDocumenUploadFromVesselDocumentTab && item?.uploadedByCustomerId == this.customerId) || this._canDeleteDocumentsFromDocumentManagementCentre) && this.isRequirementReady(item); }
                        }
                    ]
                }
            }
        });

        return columns;
    }


    private getDocumentExpiryDate(documentExpiryDate: any, startDate: any, validForDays: number): moment.Moment {
        documentExpiryDate = documentExpiryDate ? moment.utc(documentExpiryDate) : null;
        let alwaysValid = validForDays == 0;
        const expiryDateSinceStartDate = moment.utc(startDate);
        expiryDateSinceStartDate.add(validForDays, 'day');
        if (documentExpiryDate != null) {
            return documentExpiryDate < expiryDateSinceStartDate || alwaysValid ? documentExpiryDate : expiryDateSinceStartDate;
        } else {
            return alwaysValid ? null : expiryDateSinceStartDate;
        }
    }

    private getItemId(item: RequirementModel) {
        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm)
            return (<FormMetaModel>item.meta)?.vesselFormId;
        if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument)
            return (<DocumentMetaModel>item.meta)?.vesselDocumentId;
    }

    //Filter by
    public filterBy(type: string) {
        this.selectedFilterType = type;

        this.applyFilterOnDocuments();

        this.itemSource = {
            source: this._filterdRequirements,
            sourceType: SourceType.Collection,
            columns: this.getColumns(),
            getRowClass: (item: RequirementModel, rowItem) => {
                return this.getExpiredClass(item);
            },
            initialSorting: this.getSortingColumns()
        };
    }

    private downloadDocument(item: RequirementModel, name: string): void {
        this.documentManagementApi.downloadDocument(this.getItemId(item), name);
    }

    private getExpiredClass(item: RequirementModel) {
        if (this.isRequirementExpired(item)) return 'is-expired';

        return '';
    }

    public getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A';

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return val ? moment.utc(val).fromNow() : null;
    }

    //Open upload document dialog
    private openUploadDocumentDialog(item: RequirementModel) {

		//Send event to google analytics
		this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_upload_docs_submit_documents", {
            "charterer": item?.requirementCustomerName ?? "N/a"
        });

        //Open manage access dialog
        this.openManageAccessDialog(item, false);
    }

    private openManageAccessDialog(item: RequirementModel, isEditing: boolean = true) {
        this.dialogService.show(DocumentManagementDialogConstants.ManageAccessDialog, {
            documentId: this.getItemId(item),
            documentTypeId: (<DocumentMetaModel>item.meta).documentTypeId,
            defaultAccessTypeId: item.defaultAccessTypeId ?? AccessTypeEnum.AccessOnDemand,
            vesselId: this.vesselId,
            vesselName: this.vessel?.vesselName,
            vesselIdeintifier: this.vessel?.internationalMaritimeOrganizationNumber
                ?? this.vessel?.vehicleIdentificationNumber
                ?? this.vessel?.europeanNumberOfIdentification
                ?? this.vessel?.maritimeMobileServiceIdentityNumber,
            isEditing: isEditing,
            isExpired: this.isRequirementExpired(item),
            refreshData: () => { this.refreshData(); }
        }, false, false, false, 'right');
    }

    private openRequestAccessDialog(item: RequirementModel) {

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_view_docs_request_access_dialog", {
            "charterer": item.requirementCustomerName,
            "documentName": this.getDocumentName(item)
        });

        //Open request access dialog
        this.dialogService.show(DocumentManagementDialogConstants.RequestAccessDialog,
            {
                isForm: item.entityType == DocumentManagementEntityTypeEnum.VesselForm,
                itemId: this.getItemId(item),
                vesselId: this.vesselId, vesselName: this.vessel?.vesselName,
                vesselImo: this.vessel?.internationalMaritimeOrganizationNumber
                    ?? this.vessel?.vehicleIdentificationNumber
                    ?? this.vessel?.europeanNumberOfIdentification
                    ?? this.vessel?.maritimeMobileServiceIdentityNumber,
                documentName: this.getDocumentName(item),
                requirementCustomerName: item.requirementCustomerName,
                onSave: () => { this.refreshData(); }
            }, true);
    }

    private async loadDocumentNames() {
        const formsLookupQuery = "OnboardingConfigs";
        const documentsLookupQuery = "AttachmentTypes";
        const lookupParams = (item: RequirementModel) => {
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm)
                return `${item.requirementCustomerId}`;
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument)
                return `${DocumentManagementEntityTypeEnum.DocumentRequirementSubscriptionVessel}, false`;
        };
        const lookupQuery = (item: RequirementModel) => {
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm)
                return formsLookupQuery;
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument)
                return documentsLookupQuery;
        };

        const lookups = this._documentsAndForms.map(x => {
            return {
                query: lookupQuery(x),
                params: lookupParams(x)
            }
        });

        const lookupQueries = Enumerable.from(lookups).groupBy(x => { return { query: x.query, params: x.params }; }, null,
            (key, group) => { return { query: key.query, params: key.params }; },
            (key) => { return `${key.query}__${key.params}`; }
        );

        let promises: Promise<LookupValueResponse>[] = [];
        lookupQueries.forEach(x => {
            const promise = this.lookupClientService.getLookup(new LookupValueRequest(x.query, x.params));
            promises.push(promise);
        });

        const responses = await Promise.all(promises);

        responses.forEach(response => {
            if (response.lookupValueQuery == formsLookupQuery) {
                response.activeValues.forEach(x => {
                    this._formTypes[x.id] = x.name;
                });
            }
            else if (response.lookupValueQuery == documentsLookupQuery) {
                response.activeValues.forEach(x => {
                    this._documentTypes[x.id] = x.name;
                });
            }
        });
    }

    private async getReviews(customerId?: number)
    {
        try {
            if(this.isRsUser && !customerId)
            {
                customerId = -1; // load reviews for all customers
            }
            if(this._customerReviews[customerId ?? this.customerId])
                return this._customerReviews[customerId ?? this.customerId];

            const response = await this.documentReviewApi.getDocumentReviews(this.vesselId, customerId);
            const reviews = Enumerable.from(response?.reviews ?? []).orderByDescending(x => x.reviewedDate).toArray();
            this._customerReviews[customerId ?? this.customerId] = reviews;

            return reviews;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }

    private async refreshData(initialRefresh: boolean = false) {
        let documentsAndForms = [];
        let tasks: Promise<any>[] = [];
        tasks.push(this.docuementRequirementApi.getDocumentsAndFormsForVessel(this.vesselId));
        tasks.push(this.getReviews());
        const tasksResults = await Promise.all(tasks);
        const documentsAndFormsResponse = tasksResults[0];
        const reviewsResponse = tasksResults[1];
        documentsAndForms.push(...documentsAndFormsResponse.documents.map((document) => {
            const reviews = reviewsResponse
            ?.filter(x => x.entityTypeId == DocumentManagementEntityTypeEnum.VesselDocument 
                    && x.id == this.getItemId(document)
            );
            document.review = reviews && reviews?.length > 0? reviews[0]: null;
            return document;
        }));
        documentsAndForms.push(...documentsAndFormsResponse.forms.map((form) => {
            const reviews = reviewsResponse
            ?.filter(x => x.entityTypeId == DocumentManagementEntityTypeEnum.VesselForm 
                    && x.id == this.getItemId(form)
            );
            form.review = reviews && reviews?.length > 0? reviews[0]: null;
            return form;
        }));

        this._documentsAndForms = documentsAndForms;

        if (initialRefresh && this.requirementsCategories.some(x => x.requirementId == this.selectedRequirementId)) {
            this.selectedFilterType = this.selectedRequirementId.toString();
        }

        this.applyFilterOnDocuments();

        await this.loadDocumentNames();

        this.itemSource = {
            source: this._filterdRequirements,
            sourceType: SourceType.Collection,
            columns: this.getColumns(),
            getRowClass: (item: RequirementModel, rowItem) => {
                return this.getExpiredClass(item);
            },
            initialSorting: this.getSortingColumns()
        };
    }

    private async completeVesselForm(item: RequirementModel) {
        item.actionLoading = true;
        try {
            let formMeta = item.meta as FormMetaModel;
            if (formMeta.onboardingRequestId) {
                window.open(formMeta.formSubsmissionUrl, "_blank");
            }
            else {
                var url = await this.documentManagementApi.saveVesselForm({
                    vesselId: this.vesselId,
                    formTypeId: (<FormMetaModel>item.meta).formTypeId
                });

                window.open(url, "_blank");
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            item.actionLoading = false;
        }
    }

    getSortingColumns() {
        const cols: DataGridColumn[] = [
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 1,
                name: "requirementName",
                dataType: DataType.Default,
                isSortable: true,
                isAscending: true,
                sortFunction: (a: RequirementModel, b: RequirementModel, columnName: string) => {
                    const requireItemA = !this.isRequirementReady(a);
                    const requireItemB = !this.isRequirementReady(b);
                    const expiredItemA = this.isRequirementExpired(a);
                    const expiredItemB = this.isRequirementExpired(b);
                    const strCompare = this.getDocumentName(a).localeCompare(this.getDocumentName(b)); //order by name
                    const requiredCompare = requireItemA && !requireItemB ? -1 : requireItemB && !requireItemA ? 1 : 0;// order by requireing item
                    const expiredCompare = expiredItemA && !expiredItemB ? -1 : expiredItemB && !expiredItemA ? 1 : 0;// order by expired item

                    return requiredCompare == 0 ? //if both have item or don't have item, order by expired, then by name. else we order those that require item first
                        (expiredCompare == 0 ?  //if both are expired or not expired, order by name. else we order those that are expired first
                            strCompare : expiredCompare)
                        : requiredCompare;
                }
            }
        ];

        return cols;
    }

    private shouldRequestDocumentAccess = (item: RequirementModel) => {
        const accessOnDemand = AccessTypeEnum.AccessOnDemand;
        return !this.isRsUser && !item.hasAccess && !item.hasPendingAccessRequest &&
            (item.defaultAccessTypeId == accessOnDemand || item.accessTypes?.filter(x => x.customerId == this.customerId && x.accessTypeId == accessOnDemand)?.length > 0);
    }

    private hasDocumentAccess = (item: RequirementModel) => {
        return this.isRequirementReady(item) && (this.isRsUser || item.hasAccess);
    }

    private applyFilterOnDocuments() {
        if (this.selectedFilterType == 'all-documents') {
            this._filterdRequirements = this.getAllDocuments();
        } else if (this.selectedFilterType == 'pending-documents') {
            this._filterdRequirements = this.getPendingDocuments();
        } else {
            const requirementId = isNaN(parseInt(this.selectedFilterType)) ? null : parseInt(this.selectedFilterType);
            this._filterdRequirements = this.getDocumentsByRequirementId(requirementId);
        }

        this._filterdRequirements = this.filterUniqueRequirements(this._filterdRequirements);
    }

    filterUniqueRequirements(list: RequirementModel[])
    {
        list = Enumerable.from(list).groupBy(x => x.entityType == DocumentManagementEntityTypeEnum.VesselForm?
            `f_${(<FormMetaModel>x.meta).formTypeId}`:
            `d_${(<DocumentMetaModel>x.meta).documentTypeId}`
        ).select(x => {
            const requirement = x.orderBy(r => r.documentValidityInDays == 0? 999999: r.documentValidityInDays).first();
            requirement.requestingParties = x?.select(r => r.requirementCustomerId)?.distinct()?.toArray();
            return requirement;
        }).toArray();

        return list;
    }

    private getDocumentsByRequirementId(requirementId: number) {
        const requirements = this._documentsAndForms.filter(x => x.requirementId == requirementId).slice(0);
        if(this.isRsUser && !isNaN(parseInt(this.selectedFilterType)))
        {
            const customerId = this._documentsAndForms.filter(x => x.requirementId == requirementId)?.[0]?.requirementCustomerId;
            this.reloadReviews(customerId);
        }
        return requirements;
    }

    private async reloadReviews(customerId: number)
    {
        const reviewsResponse = await this.getReviews(customerId);
        const loadedAllReviews = this.isRsUser && (!customerId || customerId < 0);

        this._filterdRequirements
            .filter(requirement => loadedAllReviews || requirement.requirementCustomerId == customerId)
            .forEach(document => {
                const reviews = reviewsResponse
                    ?.filter(x => x.entityTypeId == document.entityType 
                            && x.id == this.getItemId(document)
                    );
                document.review = reviews && reviews?.length > 0? reviews[0]: null;
            });
        this.dataGridVm?.reloadData();
    }

    private getAllDocuments() {
        let alldocs = this._documentsAndForms.filter(x => this.isRequirementReady(x)).slice(0);
        alldocs = this.filterUniqueRequirements(alldocs);
        return alldocs;
    }

    private getPendingDocuments() {
        //If there is no document or form that is not subscribed to the customer, return 0
        if (this._documentsAndForms.every(x => x.subscribedCustomerId != this.customerId)) {
            return [];
        }
        let pendingRequirements = this._documentsAndForms.filter(x => x.subscribedCustomerId == this.customerId && (this.isRequirementExpired(x) || !this.isRequirementReady(x)));
        pendingRequirements = this.filterUniqueRequirements(pendingRequirements);
        return pendingRequirements;
    }

    private isRequirementExpired(item: RequirementModel) {
        const issuedDate = item.entityType == DocumentManagementEntityTypeEnum.VesselForm ? null : (<DocumentMetaModel>item.meta).issueDate;
        const startDate = issuedDate ?? item.uploadedDate;
        const expiryDate = startDate ? this.getDocumentExpiryDate(item.meta.expiryDate, startDate, item.documentValidityInDays) : null;
        return expiryDate && moment.utc(expiryDate).isBefore(moment());
    }

    private canCompleteVesselForm(item: RequirementModel) {
        return !this.isRequirementReady(item) && item.entityType == DocumentManagementEntityTypeEnum.VesselForm &&
            item.subscribedCustomerId == this.customerId;
    }

    private canUploadDocument(item: RequirementModel) {
        return !this.isRequirementReady(item) && item.entityType == DocumentManagementEntityTypeEnum.VesselDocument &&
            item.subscribedCustomerId == this.customerId;
    }

    private canSeeDocumentAccess(item: RequirementModel) {
        return this.isRsUser || item.subscribedCustomerId == this.customerId;
    }

    private isRequirementReady(item: RequirementModel): boolean {
        if (!item.meta) {
            return false;
        }

        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
            const meta: FormMetaModel = item.meta as FormMetaModel;
            return meta.onboardingRequestId > 0 && meta.formStatus != 'Pending Form Input';
        } else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
            const meta: DocumentMetaModel = item.meta as DocumentMetaModel;
            return meta.entityAttachmentIds?.length > 0;
        }
    }

    //Open review dialog
    public openReviewDialog(item: RequirementModel, isMarkAsReviewed: boolean) {
        this.dialogService.show(DocumentManagementDialogConstants.ReviewDocumentDialog, {
            itemId: this.getItemId(item),
            typeId: item.entityType,
            isMarkAsReviewed: isMarkAsReviewed,
            requirementName: item.requirementName,
            requirementCustomerName: item.requirementCustomerName,
            onSave: (review: ReviewClientModel) => { 
                item.review = review;
                this.dataGridVm?.reloadData();
            }
        });
    }
}
